import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Category } from '../Accounts/Accounts.interface';
import { merge, isEmpty } from 'lodash';
import { firestore } from '../../utils/firebase';
import { toast } from 'react-toastify';
import { TextField } from '@material-ui/core';
import CategoriesList from '../../components/CategoriesList/CategoriesList';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers';
import {
  typeMsg,
  requiredMsg,
  minLengthMsg,
  maxLengthMsg,
  ltMsg,
  minValueMsg,
} from '../../utils/error_messages';
import './add_account.scss';
import { countries } from '../../utils/countries';
import CountryPicker from '../../components/CountryPicker/CountryPicker';

const yupSchema = yup.object({
  name: yup
    .string()
    .typeError(typeMsg('اسم العرض', 'أحرف'))
    .required(requiredMsg('اسم العرض'))
    .min(2, minLengthMsg('اسم العرض', 2))
    .max(20, maxLengthMsg('اسم العرض', 20)),
  username: yup
    .string()
    .required(requiredMsg('المعرف'))
    .matches(
      /^[A-Za-z][A-Za-z0-9._-]*$/,
      typeMsg('المعرف', 'أحرف انجليزية وأرقام أو - أو _ أو . ولايحتوي على @'),
    )
    .typeError(typeMsg('المعرف', 'أحرف انجليزية وأرقام'))
    .min(3, minLengthMsg('المعرف', 3))
    .max(20, maxLengthMsg('المعرف', 20)),
  age: yup
    .number()
    .typeError(typeMsg('العمر', 'أرقام'))
    .required(requiredMsg('العمر'))
    .min(10, minValueMsg('العمر', 10))
    .max(150, ltMsg('العمر', 150)),
  gender: yup
    .string()
    .typeError(typeMsg('الجنس', 'أحرف'))
    .required(requiredMsg('الجنس'))
    .oneOf(['male', 'female'])
    .min(2, minLengthMsg('الجنس', 2)),
  bio: yup
    .string()
    .typeError(typeMsg('البايو', 'أحرف'))
    .required(requiredMsg('البايو'))
    .min(5, minLengthMsg('البايو', 5))
    .max(50, maxLengthMsg('البايو', 50)),
});

const AddAccount: React.FC = () => {
  const history = useHistory();

  const { register, handleSubmit, watch, setValue, errors } = useForm({
    defaultValues: {
      name: '',
      username: '',
      categoryId: '',
      bio: '',
      age: 10,
      gender: 'male',
      country: countries[0].nameAr,
      city: countries[0].cities[0],
      views: 0,
      likes: 0,
      createdAt: Date.now(),
    },
    resolver: yupResolver(yupSchema),
  });

  const [category, setCategory] = useState<Category>();

  useEffect(() => {
    // register values that have no field
    register('categoryId');
    register('likes');
    register('views');
    register('createdAt');
  }, [register]);

  const onSubmit = (data: any) => {
    merge(data, { categoryId: category!.id });

    firestore
      .collection('accounts')
      .doc(data.username)
      .set(data)
      .then(() => {
        toast.success('Account added successfully!');
        history.push({ pathname: `/accounts/${category!.id}` });
      });
  };

  return (
    <div className="main-wrapper">
      <div className="form-wrapper">
        <h3>رجاءً اختر القسم من القائمة أدناه ثم ادخل البيانات</h3>
        <CategoriesList onCategorySelect={setCategory} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <CountryPicker
            showCities
            countriesLabel="البلد"
            citiesLabel="المدينة"
            selectedCountry={watch('country')}
            selectedCity={watch('city')}
            onCountrySelect={(val) => setValue('country', val)}
            onCitySelect={(val) => setValue('city', val)}
            countryRef={register}
            cityRef={register}
          />
          <TextField
            variant="outlined"
            label="اسم العرض"
            id="name"
            name="name"
            inputRef={register}
            size="small"
            error={!isEmpty(errors.name)}
            helperText={errors.name?.message}
            className="text-input"
          />
          <br />
          <TextField
            label="اسم المستخدم/المعرف"
            variant="outlined"
            id="username"
            name="username"
            inputRef={register}
            size="small"
            error={!isEmpty(errors.username)}
            helperText={errors.username?.message}
            className="text-input"
            inputProps={{ style: { direction: 'ltr' } }}
          />
          <br />
          <div className="age-gender-wrapper">
            <TextField
              label="العمر"
              variant="outlined"
              id="age"
              name="age"
              type="number"
              inputRef={register}
              size="small"
              error={!isEmpty(errors.age)}
              helperText={errors.age?.message}
              className="age-field"
              inputProps={{ style: { textAlign: 'center' } }}
            />

            <div className="gender-wrapper">
              <span className="gender-radio-container">
                <label>
                  <input
                    className="gender-radio"
                    type="radio"
                    value="male"
                    name="gender"
                    ref={register}
                  />
                  ذكر
                </label>
              </span>
              <span className="gender-radio-container">
                <label>
                  <input
                    className="gender-radio"
                    type="radio"
                    value="female"
                    name="gender"
                    ref={register}
                  />
                  أنثى
                </label>
              </span>
            </div>
          </div>
          <TextField
            label="نبذة مختصرة (البايو)"
            variant="outlined"
            id="bio"
            name="bio"
            rowsMax={2}
            inputRef={register}
            size="small"
            error={!isEmpty(errors.bio)}
            helperText={errors.bio?.message}
            className="text-input"
          />
          <br />
          <input
            type="submit"
            className="add-acc-button"
            value={`أضف الحساب`}
          />
        </form>
      </div>
    </div>
  );
};

export default AddAccount;
